/* istanbul ignore file */
import { SettingsResponse } from '@api/settings'
import config from '@config'

const PREMIUM_FARE_CLASSES = ['First Class', 'Business Class'] as const

const isPremiumFareClass = (category: string | undefined): boolean => {
  return PREMIUM_FARE_CLASSES.includes(category as any)
}

const getFixedClass = (
  retailerPartnerNumber: RetailerNumber,
  params: Record<string, any>,
  settings: SettingsResponse,
): string | null => {
  const carrierCode = params.marketingCarrierCode
  const isAmendment = params.bookingId
  const rpnSetting = config.forcedFareClass[retailerPartnerNumber]
  const carrierSetting = config.forcedFareClass[retailerPartnerNumber]?.[carrierCode]
  const supportList = config.forcedFareClass[retailerPartnerNumber]?.supported
  const isOneWay = params.returnDate == null && params.returnDepartureDate == null
  const isFareClassSelection = settings?.fareClasses.displayOn === 'search_results'

  const setting = carrierSetting ?? rpnSetting

  if (isFareClassSelection && supportList && params.fareClass) return params.fareClass
  if (!setting || isAmendment) return null
  if (isOneWay) return setting.oneWay
  else return setting.twoWay
}

const getCheckoutFixedClass = (
  retailerPartnerNumber: RetailerNumber,
  params: Record<string, any>,
  settings: SettingsResponse,
): string | null => {
  const setting = getFixedClass(retailerPartnerNumber, params, settings)
  const forcedDefaultClass = config.forcedFareClass[retailerPartnerNumber]?.checkout?.default
  const forcedDefinedClass = config.forcedFareClass[retailerPartnerNumber]?.checkout?.[params.fareClass]
  const weeklyForcedCheckoutClass = params.weekly && config.forcedFareClass[retailerPartnerNumber]?.weekly

  const supportList = config.forcedFareClass[retailerPartnerNumber]?.supported
  const isFareClassSelection = settings?.fareClasses.displayOn === 'search_results'

  if (supportList && isFareClassSelection && forcedDefinedClass) return forcedDefinedClass
  if (weeklyForcedCheckoutClass) return weeklyForcedCheckoutClass

  return forcedDefaultClass ?? setting
}

const getFixedClassSupportedList = (retailerPartnerNumber: RetailerNumber, isReturn: boolean): string[] | null => {
  const base = config.forcedFareClass[retailerPartnerNumber]
  const direction = isReturn ? 'twoWay' : 'oneWay'

  if (!base?.supported?.[direction]) return null

  return base.supported[direction]
}

const getMedia = (media: Media.Data, fareClass: string): string[] =>
  media.fare_classes?.reduce<string[]>(
    (acc, { code, images, videos }) => (code === fareClass ? [...acc, ...images, ...videos] : acc),
    [],
  )

const getYoutubeId = (url: string): string | undefined => {
  const regex = /(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/

  return url.match(regex)?.[1]
}

const validateVideoUrl = (url: string): boolean => {
  const regex = /\bhttps?:\/\/\S+\.(mp4|avi|mov|wmv|flv|mkv|webm)\b/

  return regex.test(url)
}

export const getCheapestSecondClass = (fares: Fare[], cheapestFareClassCode: string): Fare => {
  const foundFare = fares.find(fare => fare.fareClass.code === cheapestFareClassCode)
  if (!foundFare) {
    throw new Error(`No fare found with class code: ${cheapestFareClassCode}`)
  }
  return foundFare
}

export const getCheapestFirstClassPrice = (fares: Fare[]): Fare | undefined => {
  const firstClassFares = fares.filter(fare => isPremiumFareClass(fare.fareClass?.iataCategory))

  if (firstClassFares.length === 0) return undefined

  return firstClassFares.reduce((minFare, currentFare) => {
    return currentFare.originalPrice < minFare.originalPrice ? currentFare : minFare
  }, firstClassFares[0])
}

export const getFareClasses = (fares: Fare[]) => {
  const firstClassFares: typeof fares = []
  const secondClassFares: typeof fares = []

  for (const fare of fares) {
    const category = fare.fareClass?.iataCategory
    if (isPremiumFareClass(category)) {
      firstClassFares.push(fare)
    } else if (category === 'Economy Class') {
      secondClassFares.push(fare)
    }
  }

  firstClassFares.sort((a, b) => a.originalPrice - b.originalPrice)
  secondClassFares.sort((a, b) => a.originalPrice - b.originalPrice)

  return { firstClassFares, secondClassFares }
}

const isOpenReturnFareClass = (fareClass: FareClass): boolean => {
  return fareClass.journeyType === 'open_return'
}

const hasOpenReturnFareClass = (fareClasses: FareClass[] = []): boolean => fareClasses.some(isOpenReturnFareClass)

const getOpenReturnFareClass = (fareClasses: FareClass[] = []): FareClass | null => {
  return (
    fareClasses.find(fareClass => {
      if (isOpenReturnFareClass(fareClass)) {
        return fareClass
      }
    }) || null
  )
}

const getFareClassByCode = (code: string, marketingCarriers: MarketingCarrier[]) => {
  const mergedFareClasses = [...new Set(marketingCarriers.flatMap(carrier => carrier.fareClasses || []))]

  return mergedFareClasses.find(fareClass => fareClass.code === code)
}

const fareClassUtils = {
  getFixedClass,
  getCheckoutFixedClass,
  getMedia,
  getYoutubeId,
  validateVideoUrl,
  getFixedClassSupportedList,
  hasOpenReturnFareClass,
  getOpenReturnFareClass,
  isOpenReturnFareClass,
  getFareClassByCode,
}

export default fareClassUtils
